import { isScrollLocked } from "core/scroll-lock";
import { SlideDrawerElement } from "system/slide-drawer";

class StickyHeader extends HTMLElement {
  connectedCallback() {
    this.header = document.querySelector(".section-header");
    this.alwaysSticky = this.dataset.stickyType === "always";
    this.bounds = {};
    this.scrollTop = 0;
    this.preventReveal = false;

    this.setHeight();

    if (this.classList.contains("header-wrapper--transparent")) {
      document.body.classList.add("header-is-transparent");
    }

    this._mq = window.matchMedia("(max-width: 990px)");
    this._onResize = () => this.setHeight();
    this._mq.addEventListener("change", this._onResize);

    if (this.alwaysSticky && this.header) {
      this.header.classList.add("shopify-section-header-sticky");
    }

    // rAF-gate: coalesce a burst of scroll events into one class-write per frame.
    this._scrollRaf = 0;
    this._onScroll = () => {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = 0;
        this.handleScroll();
      });
    };
    window.addEventListener("scroll", this._onScroll, { passive: true });

    // handleScroll() already no-ops while scroll-locked, but that only freezes
    // whatever hidden/visible state the header was already in — if the user
    // had scrolled down (header hidden) right before opening an overlay that
    // locks scroll (search, drawer, cart), the header stayed hidden the whole
    // time, leaving a visible gap above panels anchored to --header-offset
    // (e.g. the search panel). Force a reveal the instant the lock engages.
    this._onScrollLock = () => {
      this.classList.add("scroll-lock-active");
      this.header?.classList.remove("shopify-section-header-hidden");
    };
    this._onScrollUnlock = () => this.classList.remove("scroll-lock-active");
    document.addEventListener("theme:scroll-lock", this._onScrollLock);
    document.addEventListener("theme:scroll-unlock", this._onScrollUnlock);

    if (this.header) {
      const observer = new IntersectionObserver((entries) => {
        this.bounds = entries[0].intersectionRect;
        observer.disconnect();
      });
      observer.observe(this.header);
    }

    this.#bindHighlight();
  }

  // Píldora de campaña programada: el Liquid ya decidió al pintar, pero la página
  // puede venir de caché o seguir abierta cuando la campaña empieza o acaba. En el
  // editor no se toca: allí se ve siempre, atenuada fuera de fechas.
  #bindHighlight() {
    const pill = this.querySelector(".header__highlight");
    // Con una fecha mal escrita el Liquid ya la dejó apagada y así se queda.
    if (!pill || window.Shopify?.designMode || pill.hasAttribute("data-schedule-error")) return;
    const start = Date.parse(pill.dataset.start || "");
    const end = Date.parse(pill.dataset.end || "");
    if (Number.isNaN(start) && Number.isNaN(end)) return;

    const sync = () => {
      const now = Date.now();
      const off = (!Number.isNaN(start) && now < start) || (!Number.isNaN(end) && now >= end);
      pill.hidden = off;
      pill.classList.toggle("is-off-schedule", off);
      const next = [start, end].filter((t) => !Number.isNaN(t) && t > now).sort((a, b) => a - b)[0];
      clearTimeout(this._pillTimer);
      // `setTimeout` no pasa de ~24,8 días; más lejos, la página se habrá recargado.
      if (next && next - now < 2 ** 31 - 1) this._pillTimer = setTimeout(sync, next - now + 500);
    };
    sync();
  }

  disconnectedCallback() {
    clearTimeout(this._pillTimer);
    document.body.classList.remove("header-is-transparent");
    if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    window.removeEventListener("scroll", this._onScroll);
    this._mq.removeEventListener("change", this._onResize);
    document.removeEventListener("theme:scroll-lock", this._onScrollLock);
    document.removeEventListener("theme:scroll-unlock", this._onScrollUnlock);
  }

  setHeight() {
    // El top bar es su propia sección, encima de esta, pero va fijo con la cabecera
    // (`.section-top-bar` en header.css): su alto cuenta en `--header-height`.
    const topBar = document.querySelector(".header__top-bar");
    const mainBar = this.querySelector(".header");
    const topBarHeight = topBar?.offsetHeight ?? 0;
    const barHeight = mainBar?.offsetHeight ?? 0;
    const total = topBarHeight + (this.header?.offsetHeight ?? barHeight);

    document.documentElement.style.setProperty("--header-top-bar-height", `${topBarHeight}px`);
    document.documentElement.style.setProperty("--header-bar-height", `${barHeight}px`);
    document.documentElement.style.setProperty("--header-height", `${total}px`);
    document.documentElement.style.setProperty("--header-offset", `${total}px`);
  }

  handleScroll() {
    if (!this.header) return;
    if (
      isScrollLocked() ||
      this.classList.contains("scroll-lock-active") ||
      this.classList.contains("header-menu-open")
    ) {
      return;
    }

    const y = window.scrollY;

    if (this.classList.contains("header-wrapper--transparent")) {
      this.classList.toggle("is-scrolled", y > 20);
    }

    if (y > this.scrollTop && y > this.bounds.bottom) {
      this.header.classList.add("scrolled-past-header");
      if (this.alwaysSticky) return;
      this.header.classList.add("shopify-section-header-hidden", "shopify-section-header-sticky");
    } else if (y < this.scrollTop && y > this.bounds.bottom) {
      this.header.classList.add("scrolled-past-header");
      if (this.alwaysSticky) return;
      if (!this.preventReveal) {
        this.header.classList.add("shopify-section-header-sticky");
        this.header.classList.remove("shopify-section-header-hidden");
      }
    } else if (y <= this.bounds.top) {
      this.header.classList.remove("scrolled-past-header", "shopify-section-header-hidden", "shopify-section-header-sticky");
    }

    this.scrollTop = y;
  }
}

/**
 * Top bar — la sección `announcement-bar` del grupo de cabecera. Es su propio
 * elemento, y no parte de `sticky-header`, para que el editor lo reenganche al
 * volver a pintar SOLO esta sección. Avisa a la cabecera cada vez que cambia de
 * alto (y al salir), porque ella suma ese alto a `--header-height` y se pega
 * debajo: un alto viejo —medido antes de la fuente o a otro ancho— dejaba un hueco.
 *
 * Dos formas de mover los mensajes, según `data-layout`:
 * - `one` / `three` — CINTA: cada `data-interval` ms la fila avanza un mensaje a
 *   la izquierda y el que sale pasa al final. Vale para cualquier número de
 *   mensajes: nunca queda una «página» a medias. Si caben todos, no se mueve.
 *   Cuántos se ven lo decide el CSS (`--top-bar-per-view`), que en móvil baja a 1.
 * - `marquee` — MARQUESINA: los mensajes se repiten hasta cubrir el ancho, el
 *   grupo se duplica y una animación CSS lo desplaza a velocidad constante.
 *
 * Sin botón de pausa, por decisión de diseño: la barra se detiene con el puntero
 * encima o con el foco dentro, y con «reducir movimiento» la cinta cambia sin
 * deslizar y la marquesina se queda quieta.
 */
class HeaderTopBar extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector(".header__top-bar-track");
    this.viewport = this.querySelector(".header__top-bar-viewport");
    this.reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    this.holds = new Set();

    this.#bindTopBarCodes();
    this.#bindHolds();
    if (this.dataset.layout === "marquee") this.#buildMarquee();
    else this.#startStrip();

    this._resize = new ResizeObserver(() => {
      document.querySelector("sticky-header")?.setHeight?.();
      if (this.dataset.layout === "marquee") this.#buildMarquee();
    });
    this._resize.observe(this);
  }

  disconnectedCallback() {
    clearInterval(this._timer);
    this._resize?.disconnect();
    document.querySelector("sticky-header")?.setHeight?.();
  }

  #hold(reason, on) {
    if (on) this.holds.add(reason);
    else this.holds.delete(reason);
    this.classList.toggle("is-paused", this.holds.size > 0);
  }

  #bindHolds() {
    this.addEventListener("pointerenter", () => this.#hold("pointer", true));
    this.addEventListener("pointerleave", () => this.#hold("pointer", false));
    this.addEventListener("focusin", () => this.#hold("focus", true));
    this.addEventListener("focusout", (event) => {
      if (!this.contains(event.relatedTarget)) this.#hold("focus", false);
    });

    // Editor: el mensaje seleccionado se trae a la vista y la barra se detiene
    // mientras se edita; si no, el texto que se escribe se iba de la pantalla.
    this.addEventListener("shopify:block:select", (event) => {
      this.#hold("editor", true);
      const item = event.target instanceof Element ? event.target.closest(".header__top-bar-item") : null;
      if (item && this.dataset.layout !== "marquee") this.#bringToFront(item);
    });
    this.addEventListener("shopify:block:deselect", () => this.#hold("editor", false));
  }

  #perView() {
    return Number.parseInt(getComputedStyle(this).getPropertyValue("--top-bar-per-view"), 10) || 1;
  }

  #startStrip() {
    const interval = Number(this.dataset.interval) || 4000;
    clearInterval(this._timer);
    this._timer = setInterval(() => {
      if (this.holds.size || document.hidden || this._moving) return;
      this.#step();
    }, interval);
  }

  #recycle(first) {
    const track = this.track;
    track.style.transition = "none";
    track.style.transform = "";
    track.append(first);
    void track.offsetWidth;
    track.style.transition = "";
  }

  #step() {
    const track = this.track;
    const first = track?.firstElementChild;
    if (!first || track.children.length <= this.#perView()) return;

    if (this.reduce) {
      this.#recycle(first);
      return;
    }

    this._moving = true;
    const done = () => {
      clearTimeout(fallback);
      track.removeEventListener("transitionend", onEnd);
      this.#recycle(first);
      this._moving = false;
    };
    const onEnd = (event) => {
      if (event.target === track && event.propertyName === "transform") done();
    };
    // `transitionend` no llega si la pestaña se oculta a mitad: plan B por tiempo.
    const fallback = setTimeout(done, 1500);
    track.addEventListener("transitionend", onEnd);
    track.style.transform = `translateX(${-first.offsetWidth}px)`;
  }

  #bringToFront(item) {
    const track = this.track;
    if (!track?.contains(item)) return;
    track.style.transform = "";
    let guard = track.children.length;
    while (track.firstElementChild !== item && guard-- > 0) track.append(track.firstElementChild);
  }

  #buildMarquee() {
    const track = this.track;
    const viewport = this.viewport;
    if (!track || !viewport) return;
    const width = viewport.clientWidth;
    if (!width || width === this._marqueeWidth) return;
    this._marqueeWidth = width;

    for (const copy of track.querySelectorAll("[data-clone]")) copy.remove();
    track.classList.remove("is-running");
    if (this.reduce) return;

    const originals = [...track.children];
    if (!originals.length) return;

    // Las copias son decorado: fuera del árbol accesible, sin foco y sin el
    // atributo del editor, que si no vería el mismo bloque varias veces.
    const clone = (node) => {
      const copy = node.cloneNode(true);
      copy.dataset.clone = "";
      copy.setAttribute("aria-hidden", "true");
      copy.removeAttribute("data-shopify-editor-block");
      for (const el of copy.querySelectorAll("[tabindex]")) el.setAttribute("tabindex", "-1");
      return copy;
    };

    // Un grupo tiene que cubrir el ancho visible; después se duplica entero para
    // que el -50% de la animación empalme sin salto.
    let guard = 0;
    while (track.offsetWidth < width && guard++ < 20) {
      for (const node of originals) track.append(clone(node));
    }
    for (const node of [...track.children]) track.append(clone(node));

    const speed = { slow: 25, normal: 40, fast: 60 }[this.dataset.speed] || 40;
    track.style.setProperty("--top-bar-marquee-duration", `${(track.offsetWidth / 2 / speed).toFixed(2)}s`);
    track.classList.add("is-running");
  }

  #bindTopBarCodes() {
    const bar = this;
    const message = this.track;
    const iconsTpl = this.querySelector("[data-header-code-icons]");
    if (!bar || !message) return;

    const copyLabel = message.dataset.copyLabel || "CODE";
    const copiedLabel = message.dataset.copiedLabel || "CODE";
    const labelFor = (template, code) => template.replaceAll("CODE", code);

    const html = message.innerHTML;
    const next = html.replace(/\{([A-Z0-9_-]+)\}/gi, (_, code) => {
      const safe = code.replaceAll('"', "");
      return `<span class="header__top-bar-code" data-copy="${safe}">${safe}</span>`;
    });
    if (next !== html) message.innerHTML = next;

    for (const pill of message.querySelectorAll("[data-copy]")) {
      const code = pill.dataset.copy;
      if (!code) continue;
      if (!pill.querySelector(".header__top-bar-code-text")) {
        const text = document.createElement("span");
        text.className = "header__top-bar-code-text";
        text.textContent = code;
        pill.textContent = "";
        pill.append(text);
      }
      if (iconsTpl && !pill.querySelector(".header__top-bar-code-icons")) {
        pill.append(iconsTpl.content.cloneNode(true));
      }
      pill.setAttribute("role", "button");
      pill.setAttribute("tabindex", "0");
      pill.setAttribute("aria-label", labelFor(copyLabel, code));
      pill.dataset.copiedLabel = labelFor(copiedLabel, code);
    }

    if (!navigator.clipboard) return;

    const copy = (pill) => {
      const code = pill.dataset.copy;
      if (!code) return;
      navigator.clipboard.writeText(code).then(() => {
        pill.classList.add("is-copied");
        const previous = pill.getAttribute("aria-label");
        if (pill.dataset.copiedLabel) pill.setAttribute("aria-label", pill.dataset.copiedLabel);
        clearTimeout(pill._copyReset);
        pill._copyReset = setTimeout(() => {
          pill.classList.remove("is-copied");
          if (previous) pill.setAttribute("aria-label", previous);
        }, 2000);
      }).catch(() => {});
    };

    bar.addEventListener("click", (event) => {
      const pill = event.target instanceof Element ? event.target.closest("[data-copy]") : null;
      if (pill && bar.contains(pill)) copy(pill);
    });
    bar.addEventListener("keydown", (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const pill = event.target instanceof Element ? event.target.closest("[data-copy]") : null;
      if (!pill || !bar.contains(pill)) return;
      event.preventDefault();
      copy(pill);
    });
  }
}

/**
 * Panel lateral de navegación — DOS COLUMNAS, las dos aquí dentro.
 *
 *   columna 1: pestañas de contexto (Todos / Escolar / Viaje) + nivel 1
 *   columna 2: el nivel 2 de la categoría abierta, que sale deslizándose
 *
 * El nivel 1 vivía en la barra de la cabecera y se movió aquí con la maqueta de
 * Porta: la barra solo lleva «Menú», la píldora, el logo y las operaciones. Eso
 * simplifica dos cosas de golpe — el aspa vuelve a ser un `[data-drawer-close]`
 * normal (antes estaba fuera y había que engancharla a nivel de documento) y las
 * filas ya no dependen de quedar por encima del panel para seguir clicables.
 *
 * No se porta a `<body>` como el carrito: nada lo pide y salir del
 * `sticky-header` cambiaría el contexto de apilamiento sin ganar nada.
 *
 * DOS NIVELES DE CONMUTACIÓN, y no se pisan: el contexto cambia el menú entero
 * (y cierra la columna 2, porque la categoría abierta pertenecía al menú que se
 * acaba de dejar); la fila de nivel 1 solo cambia lo que hay en la columna 2.
 */
class HeaderDrawer extends SlideDrawerElement {
  static slideDirection = "left";

  /** Filas de nivel 1 que tienen nivel 2. @type {HTMLElement[]} */
  #tabs = [];

  /** Subpaneles de nivel 2. @type {HTMLElement[]} */
  #panels = [];

  /** Botones de la tira de contextos. @type {HTMLElement[]} */
  #contextTabs = [];

  /** Listas de nivel 1, una por contexto. @type {HTMLElement[]} */
  #contextPanels = [];

  /** @type {string} */
  #activeKey = "";

  /** @type {HTMLElement | null} */
  #trigger = null;

  /** Quién abrió: ahí vuelve el foco al cerrar. @type {HTMLElement | null} */
  #opener = null;

  /** @type {boolean} */
  #warmed = false;

  connectedCallback() {
    // `this` y no `document`: desde que el nivel 1 vive dentro del panel, buscar a
    // nivel de documento podría enganchar filas de otro menú de la página.
    this.#tabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll("[data-nav-tab]")]);
    this.#panels = /** @type {HTMLElement[]} */ ([...this.querySelectorAll("[data-nav-panel]")]);
    this.#contextTabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll("[data-nav-context]")]);
    this.#contextPanels = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll("[data-nav-context-panel]"),
    ]);
    this.resolveTrigger();
    super.connectedCallback();
  }

  /** @param {AbortSignal} signal */
  onConnected(signal) {
    for (const tab of this.#tabs) {
      tab.addEventListener(
        "click",
        (event) => {
          // Sin JS la fila es un enlace a su categoría (y así se queda si este
          // módulo no llega a cargar). Con JS, abre su nivel 2.
          event.preventDefault();
          this.#opener = tab;
          this.selectPanel(tab.dataset.navTab ?? "");
        },
        { signal },
      );
    }

    // APERTURA POR HOVER (escritorio).
    //
    // El clic NO se retira: en táctil no hay hover y sigue siendo la única forma de
    // abrir, así que se filtra por el medio —puntero fino que puede posarse— y por el
    // tipo de puntero del propio evento, porque un toque también emite `pointerenter`.
    //
    // `#showPanel` directo: si la fila ya está activa, no-op para no reentrar.
    //
    // Y la otra mitad de la regla: una fila SIN nivel 2 lo cierra. Es lo que hace que
    // pasar de «Mochilas» a «Lo más nuevo» recoja la columna en vez de dejarla colgada,
    // mientras que moverse hacia el propio nivel 2 no la toca — allí no hay filas.
    const punteroFino = window.matchMedia("(hover: hover) and (pointer: fine)");
    for (const fila of this.querySelectorAll(".header-nav__l1")) {
      fila.addEventListener(
        "pointerenter",
        (event) => {
          if (event.pointerType === "touch") return;
          if (!punteroFino.matches) return;

          const key = /** @type {HTMLElement} */ (fila).dataset.navTab;
          if (!key) {
            this.#collapseSub();
            return;
          }
          if (this.#activeKey === key) return;
          this.#opener = /** @type {HTMLElement} */ (fila);
          this.#showPanel(key);
        },
        { signal },
      );
    }

    for (const contextTab of this.#contextTabs) {
      contextTab.addEventListener(
        "click",
        () => this.selectContext(contextTab.dataset.navContext ?? ""),
        { signal },
      );
    }

    // «Volver» de móvil: repliega la columna 2, que allí tapa la del nivel 1. En
    // escritorio el botón existe en el markup pero el CSS lo oculta.
    for (const back of this.querySelectorAll("[data-nav-back]")) {
      back.addEventListener("click", () => this.#collapseSub(), { signal });
    }

    // La franja superior del panel se ancla a la caja REAL de la barra, así que
    // un cambio de tamaño con el menú abierto hay que volver a medirlo.
    window.addEventListener(
      "resize",
      () => {
        if (this.isOpen) this.#measure();
      },
      { passive: true, signal },
    );
  }

  /** @param {AbortSignal} signal */
  bindDrawerTriggers(signal) {
    this.trigger?.addEventListener(
      "click",
      (event) => {
        event.preventDefault();
        this.#opener = this.trigger;
        if (this.isOpen) {
          this.close();
          return;
        }
        // Abre con la columna 2 CERRADA, a diferencia de cuando el nivel 1 estaba
        // en la barra: allí el panel era solo el nivel 2 y abrirlo vacío no tenía
        // sentido. Aquí la columna 1 ya trae el menú, y preseleccionar una
        // categoría sería elegir por el visitante.
        this.open();
      },
      { signal },
    );
  }

  resolveTrigger() {
    const id = this.id;
    this.#trigger = /** @type {HTMLElement | null} */ (
      (id ? document.querySelector(`[aria-controls="${id}"]`) : null) ??
        document.querySelector("[data-drawer-open]")
    );
  }

  get trigger() {
    return this.#trigger;
  }

  /**
   * Clic en una fila de nivel 1: muestra su columna 2. Si ya está abierta, se
   * queda; otra categoría intercambia el contenido sin cerrar el menú.
   *
   * No se alterna. En escritorio el hover ya abrió la columna, y un segundo
   * clic en la misma fila no puede recogerla: el menú se cierra con el aspa,
   * la hamburguesa o el velo.
   * @param {string} key
   */
  selectPanel(key) {
    if (!key) return;
    if (this.#activeKey !== key) this.#showPanel(key);
    if (!this.isOpen) this.open();
  }

  /**
   * Cambio de contexto: se cambia el menú entero, así que la columna 2 se cierra.
   * La categoría que estuviera abierta pertenece al menú que se acaba de dejar y su
   * subpanel se quedaría en pantalla sin ninguna fila que lo señalara.
   * @param {string} key
   */
  selectContext(key) {
    if (!key) return;

    for (const panel of this.#contextPanels) {
      panel.toggleAttribute("hidden", panel.dataset.navContextPanel !== key);
    }
    for (const tab of this.#contextTabs) {
      tab.setAttribute("aria-pressed", String(tab.dataset.navContext === key));
    }

    this.#collapseSub();
  }

  /** Repliega la columna 2 y deja el nivel 1 sin nada marcado. */
  #collapseSub() {
    this.#activeKey = "";
    for (const panel of this.#panels) panel.setAttribute("hidden", "");
    for (const tab of this.#tabs) tab.setAttribute("aria-expanded", "false");
    this.classList.remove("has-sub");
  }

  /** @param {string} key */
  #showPanel(key) {
    if (!key) return;
    this.#activeKey = key;

    // La clase del host es la que saca la columna 2 de detrás de la 1 (la
    // transición es CSS). Se pone solo si el subpanel existe de verdad: una clave
    // sin panel dejaría la columna abierta y vacía.
    let found = false;
    for (const panel of this.#panels) {
      const active = panel.dataset.navPanel === key;
      panel.toggleAttribute("hidden", !active);
      if (active) found = true;
    }
    for (const tab of this.#tabs) {
      tab.setAttribute("aria-expanded", String(tab.dataset.navTab === key));
    }
    this.classList.toggle("has-sub", found);

    // Cambiar de categoría con la columna scrolleada dejaba la nueva empezada por
    // la mitad.
    const sub = this.querySelector("[data-nav-sub-layer]");
    if (sub instanceof HTMLElement) sub.scrollTop = 0;
  }

  /**
   * Dónde empieza el panel. Se lee de la caja de la barra en vez de sumar
   * variables de altura porque la cabecera puede estar pegada, replegada o aún
   * en el flujo, y solo el rect sabe cuál de las tres.
   *
   * Se escribe en :root y no en la cabecera porque el panel dejó de ser
   * descendiente suyo: vive en el grupo de overlays, al final del body.
   */
  #measure() {
    const bar = document.querySelector("sticky-header .header");
    if (!(bar instanceof HTMLElement)) return;

    const rect = bar.getBoundingClientRect();
    const root = document.documentElement;
    root.style.setProperty("--nav-bar-top", `${Math.max(0, Math.round(rect.top))}px`);
    root.style.setProperty("--nav-bar-bottom", `${Math.max(0, Math.round(rect.bottom))}px`);

    // La X del panel tiene que caer donde estaba la hamburguesa, así que el panel se
    // ancla a la caja REAL del icono y no al margen de página. Coinciden hoy, pero son
    // dos cosas distintas: el botón lleva relleno y un margen negativo propios, y el
    // margen de página cambia con el ancho. Midiendo el icono, el anclaje no depende de
    // que esos números sigan cuadrando.
    const icono = document.querySelector("sticky-header .header__drawer-icon");
    if (icono instanceof HTMLElement) {
      const caja = icono.getBoundingClientRect();
      root.style.setProperty("--nav-trigger-x", `${Math.max(0, Math.round(caja.left))}px`);
    }
  }

  /**
   * Al abrir, se piden TODAS las imágenes del menú, no solo las de la pestaña
   * visible.
   *
   * Todo el árbol viene renderizado del servidor, así que cambiar de pestaña no
   * pide nada… salvo las imágenes: nacen `loading="lazy"` y una imagen dentro de
   * un panel oculto no llega a pedirse nunca, así que la segunda pestaña se
   * dibujaba con los huecos vacíos y las fotos entrando de una en una. Pasarlas a
   * `eager` desbloquea la descarga aunque sigan ocultas, y para cuando el usuario
   * cambia de pestaña ya están.
   *
   * Se hace al abrir y no antes: en una visita que no toca el menú no se descarga
   * nada. Y una sola vez.
   */
  #warmContent() {
    if (this.#warmed) return;
    this.#warmed = true;
    for (const img of this.querySelectorAll('img[loading="lazy"]')) {
      img.setAttribute("loading", "eager");
    }
  }

  onDrawerOpen() {
    this.#warmContent();
    this.#measure();
    document.querySelector("sticky-header")?.classList.add("header-menu-open");
  }

  onDrawerClose() {
    document.querySelector("sticky-header")?.classList.remove("header-menu-open");

    // Se repliega al cerrar y no al abrir: reabrir tiene que empezar limpio, y
    // hacerlo aquí evita que la columna 2 se vea desaparecer durante la salida.
    this.#collapseSub();

    // El foco vuelve a quien abrió. La comprobación de `offsetParent` se queda
    // aunque la hamburguesa ya no se esconda en escritorio: quien abre puede ser
    // una fila de nivel 1, y esas sí desaparecen al cerrar el panel.
    const back = this.#opener?.isConnected && this.#opener.offsetParent !== null ? this.#opener : this.trigger;
    back?.focus({ preventScroll: true });
  }
}

function registerHeaderElements() {
  if (!customElements.get("sticky-header")) {
    customElements.define("sticky-header", StickyHeader);
  }
  if (!customElements.get("header-top-bar")) {
    customElements.define("header-top-bar", HeaderTopBar);
  }
  if (!customElements.get("header-drawer")) {
    customElements.define("header-drawer", HeaderDrawer);
  }
}

export function initHeader() {
  registerHeaderElements();
}

registerHeaderElements();
